/**
 * PD-16475 — notifications for storefront (portal) users.
 *
 * Portal users (`public_user`) get their own notification tables
 * (`public_user_notification*`) rather than sharing the CRM user's
 * `user_notification*` tables: the two user tables share one integer id space,
 * so a single `fk_user__id` column could not tell a customer from an employee.
 * The catalog, channels and default rules for the portal side live here, next
 * to (but separate from) the CRM catalog in notification.constants.ts.
 */

/** The events a portal user can be notified about. */
export const PORTAL_NOTIFICATION_TYPE = {
	/** Staff posted a customer-visible reply on a support request the user submitted. */
	ISSUE_REPLY: 'portal_issue_reply',
	/** A support request the user submitted was resolved, reopened or started. */
	ISSUE_STATUS_CHANGED: 'portal_issue_status_changed',
} as const;

export type PortalNotificationType =
	(typeof PORTAL_NOTIFICATION_TYPE)[keyof typeof PORTAL_NOTIFICATION_TYPE];

/** Broad bucket stored on the bell row (drives the tray icon). */
export const PORTAL_NOTIFICATION_CATEGORY = {
	SUPPORT: 'support',
} as const;

export type PortalNotificationCategory =
	(typeof PORTAL_NOTIFICATION_CATEGORY)[keyof typeof PORTAL_NOTIFICATION_CATEGORY];

/**
 * Delivery channels for a portal user. Native app push is deliberately absent
 * (deferred to PD-14302); adding it later means a new value here, a new column
 * on `public_user_notification_preference`, and a branch in
 * `resolvePortalNotificationChannels` — nothing else keys off the full list.
 */
export const PORTAL_NOTIFICATION_CHANNEL = {
	IN_APP: 'in_app',
	EMAIL: 'email',
	SMS: 'sms',
	PUSH: 'push',
} as const;

export type PortalNotificationChannel =
	(typeof PORTAL_NOTIFICATION_CHANNEL)[keyof typeof PORTAL_NOTIFICATION_CHANNEL];

export interface PortalNotificationEventModel {
	type: PortalNotificationType;
	category: PortalNotificationCategory;
	/** Row label in the storefront's notification settings. */
	label: string;
	description: string;
}

/** Every event a portal user can control, in display order. */
export const PORTAL_NOTIFICATION_EVENTS: ReadonlyArray<PortalNotificationEventModel> =
	[
		{
			type: PORTAL_NOTIFICATION_TYPE.ISSUE_REPLY,
			category: PORTAL_NOTIFICATION_CATEGORY.SUPPORT,
			label: 'Replies to my support requests',
			description: 'When our team replies to a support request you submitted.',
		},
		{
			type: PORTAL_NOTIFICATION_TYPE.ISSUE_STATUS_CHANGED,
			category: PORTAL_NOTIFICATION_CATEGORY.SUPPORT,
			label: 'Status changes on my support requests',
			description:
				'When a support request you submitted is resolved, reopened or started.',
		},
	];

/** The support events the inline "Notify me about replies" control sets together. */
export const PORTAL_SUPPORT_NOTIFICATION_TYPES: ReadonlyArray<PortalNotificationType> =
	[
		PORTAL_NOTIFICATION_TYPE.ISSUE_REPLY,
		PORTAL_NOTIFICATION_TYPE.ISSUE_STATUS_CHANGED,
	];

export function isPortalNotificationType(
	value: unknown,
): value is PortalNotificationType {
	return (
		typeof value === 'string' &&
		PORTAL_NOTIFICATION_EVENTS.some((event) => event.type === value)
	);
}

export function portalNotificationCategoryFor(
	type: string,
): PortalNotificationCategory {
	return (
		PORTAL_NOTIFICATION_EVENTS.find((event) => event.type === type)?.category ??
		PORTAL_NOTIFICATION_CATEGORY.SUPPORT
	);
}

/**
 * Appended to every notification text. Carriers require an opt-out
 * instruction on automated texts, and a STOP reply is honored both by the
 * carrier and by our own stored opt-out.
 */
export const PORTAL_SMS_OPT_OUT_SUFFIX = 'Reply STOP to opt out.';

/** Shown under every "Text" checkbox a portal user can tick (the consent disclosure). */
export const PORTAL_SMS_DISCLOSURE =
	'Msg & data rates may apply. Reply STOP to opt out.';

/** One explicit preference row's channel flags. */
export interface PortalNotificationChannelFlagsModel {
	inAppEnabled: boolean;
	emailEnabled: boolean;
	smsEnabled: boolean;
	pushEnabled: boolean;
}

/** The per-user master controls and SMS consent state that gate delivery. */
export interface PortalNotificationSettingStateModel {
	notificationsPaused: boolean;
	/** E.164 phone the user agreed to receive texts on; null = no consent recorded. */
	smsConsentPhone: string | null;
	smsConsentDate: Date | string | null;
	/** Set when the user replied STOP (or a send was refused as opted out). */
	smsOptedOutDate: Date | string | null;
	/** E.164 phone the STOP came from; null = applies to any phone. */
	smsOptedOutPhone: string | null;
}

export interface ResolvePortalNotificationChannelsInputModel {
	/** The user's saved row for this event, or null to use the defaults. */
	explicitPreference: PortalNotificationChannelFlagsModel | null;
	setting: PortalNotificationSettingStateModel | null;
	hasEmail: boolean;
	/** The account's phone in E.164, or null when it has none (or it cannot be read). */
	phoneE164: string | null;
	/** Whether the user has subscribed at least one browser for push. */
	hasPushDevice: boolean;
}

export interface PortalNotificationChannelsModel {
	inApp: boolean;
	email: boolean;
	sms: boolean;
	push: boolean;
}

/**
 * The channel values shown to a user who has never saved a preference for an
 * event (owner decision, PD-16475): the bell always; email when the account
 * has an email address; texts when it has a phone number (the Text box is
 * pre-checked, and consent is recorded when the user saves or submits with it
 * ticked); browser push once they have subscribed a browser, because
 * subscribing is itself the opt-in.
 */
export function defaultPortalNotificationChannels(args: {
	hasEmail: boolean;
	hasPhone: boolean;
	hasPushDevice: boolean;
}): PortalNotificationChannelsModel {
	return {
		inApp: true,
		email: args.hasEmail,
		sms: args.hasPhone,
		push: args.hasPushDevice,
	};
}

/** Whether texting `phoneE164` was agreed to (consent is per phone number). */
export function hasPortalSmsConsent(
	setting: PortalNotificationSettingStateModel | null,
	phoneE164: string | null,
): boolean {
	return (
		setting != null &&
		phoneE164 != null &&
		setting.smsConsentDate != null &&
		setting.smsConsentPhone === phoneE164
	);
}

/**
 * Whether the user has opted out of texts on `phoneE164`. An opt-out recorded
 * without a phone applies to every number; one recorded for another number
 * does not carry over after the account's phone changes, because the carrier
 * block is per number too.
 */
export function isPortalSmsOptedOut(
	setting: PortalNotificationSettingStateModel | null,
	phoneE164: string | null,
): boolean {
	if (setting == null || setting.smsOptedOutDate == null) {
		return false;
	}
	return (
		setting.smsOptedOutPhone == null || setting.smsOptedOutPhone === phoneE164
	);
}

/**
 * Which channels one event is actually delivered on for one user. Pure, so
 * the dispatcher, the settings API and the tests share one rule:
 *
 *   1. The master pause switch silences everything.
 *   2. Each channel takes the user's saved value for the event, else the
 *      default from `defaultPortalNotificationChannels`.
 *   3. Email needs an address; push needs a subscribed browser.
 *   4. A text additionally needs recorded consent for the account's CURRENT
 *      phone and no STOP on it. A preference alone never sends a text.
 */
export function resolvePortalNotificationChannels(
	input: ResolvePortalNotificationChannelsInputModel,
): PortalNotificationChannelsModel {
	const off: PortalNotificationChannelsModel = {
		inApp: false,
		email: false,
		sms: false,
		push: false,
	};
	if (input.setting?.notificationsPaused === true) {
		return off;
	}

	const defaults = defaultPortalNotificationChannels({
		hasEmail: input.hasEmail,
		hasPhone: input.phoneE164 != null,
		hasPushDevice: input.hasPushDevice,
	});
	const explicit = input.explicitPreference;

	const inApp = explicit?.inAppEnabled ?? defaults.inApp;
	const email = input.hasEmail && (explicit?.emailEnabled ?? defaults.email);
	const smsWanted =
		input.phoneE164 != null && (explicit?.smsEnabled ?? defaults.sms);
	const sms =
		smsWanted &&
		hasPortalSmsConsent(input.setting, input.phoneE164) &&
		!isPortalSmsOptedOut(input.setting, input.phoneE164);
	const push = input.hasPushDevice && (explicit?.pushEnabled ?? defaults.push);

	return { inApp, email, sms, push };
}
